import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import { useEffect, useRef, useState } from "react";

interface ImportTemplateModalProps {
  open: boolean;
  onClose: () => void;
  onFileSelect: (file: File) => void;
  skillMode?: boolean;
}

const ACCEPTED_TYPES = new Set([
  "application/pdf",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "text/plain",
  "text/markdown",
]);

const MAX_BYTES = 10 * 1024 * 1024;

export default function ImportTemplateModal({
  open,
  onClose,
  onFileSelect,
  skillMode = false,
}: ImportTemplateModalProps) {
  const [errorMsg, setErrorMsg] = useState("");
  const [isDragOver, setIsDragOver] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) {
      setErrorMsg("");
      setIsDragOver(false);
      setSelectedFile(null);
    }
  }, [open]);

  function handleClose() {
    setErrorMsg("");
    setIsDragOver(false);
    setSelectedFile(null);
    onClose();
  }

  function pickFile(file: File) {
    const extension = file.name.split(".").pop()?.toLowerCase();
    const isAccepted = ACCEPTED_TYPES.has(file.type) || (skillMode && (extension === "md" || extension === "txt"));
    if (!isAccepted || (!skillMode && file.type !== "application/pdf" && !file.name.toLowerCase().endsWith(".docx"))) {
      setErrorMsg(skillMode ? "Only PDF, DOCX, TXT and Markdown files are supported." : "Only PDF and DOCX files are supported.");
      return;
    }
    if (file.size > MAX_BYTES) {
      setErrorMsg("File exceeds the 10 MB limit.");
      return;
    }
    setErrorMsg("");
    setSelectedFile(file);
  }

  function handleSave() {
    if (!selectedFile) return;
    onFileSelect(selectedFile);
    handleClose();
  }

  function formatFileSize(bytes: number) {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }

  function handleDrop(e: React.DragEvent<HTMLDivElement>) {
    e.preventDefault();
    setIsDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) pickFile(file);
  }

  function handleFileInput(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (file) pickFile(file);
    e.target.value = "";
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>{skillMode ? "Upload Skill" : "Import Template"}</DialogTitle>
      <DialogContent>
        <Box
          onDragOver={(e) => {
            e.preventDefault();
            setIsDragOver(true);
          }}
          onDragLeave={() => setIsDragOver(false)}
          onDrop={handleDrop}
          onClick={() => inputRef.current?.click()}
          sx={{
            border: "2px dashed",
            borderColor: isDragOver ? "primary.main" : "grey.400",
            borderRadius: 2,
            p: 6,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 2,
            cursor: "pointer",
            bgcolor: isDragOver ? "primary.50" : "grey.50",
            transition: "border-color 0.15s, background-color 0.15s",
            userSelect: "none",
          }}
        >
          <input
            ref={inputRef}
            type="file"
            accept={skillMode
              ? ".pdf,.docx,.txt,.md,application/pdf,text/plain,text/markdown,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
              : ".pdf,.docx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document"}
            style={{ display: "none" }}
            onChange={handleFileInput}
          />
          {selectedFile ? (
            <>
              <InsertDriveFileOutlinedIcon sx={{ fontSize: 48, color: "primary.main" }} />
              <Box sx={{ textAlign: "center", minWidth: 0, maxWidth: "100%" }}>
                <Typography variant="body1" fontWeight={600} noWrap title={selectedFile.name}>
                  {selectedFile.name}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  {formatFileSize(selectedFile.size)}
                </Typography>
              </Box>
              <Typography variant="body2" color="primary.main" fontWeight={500}>
                Click or drop another file to replace it
              </Typography>
            </>
          ) : (
            <>
              <UploadFileIcon sx={{ fontSize: 48, color: "grey.500" }} />
              <Typography variant="body1" fontWeight={500}>
                {skillMode ? "Drop PDF, DOCX, TXT or Markdown here" : "Drop PDF or DOCX here"}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                or click to browse
              </Typography>
            </>
          )}
        </Box>

        {errorMsg && (
          <Typography variant="body2" color="error" sx={{ mt: 2 }}>
            {errorMsg}
          </Typography>
        )}

        <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3, gap: 1 }}>
          <Button variant="outlined" onClick={handleClose}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSave} disabled={!selectedFile}>
            {skillMode ? "Save skill" : "Import"}
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
}
